import { useState } from 'react';
import { Link2, Trash2, ToggleLeft, ToggleRight, Zap, ZapOff, RefreshCw } from 'lucide-react';
import type { WalletAccount, WalletAccountMapping } from '@/api/wallet';
import { useTranslation } from 'react-i18next';

interface GroupOption {
  id: string;
  name: string;
  currency_code: string;
  color: string | null;
}

interface WalletAccountMappingListProps {
  accounts: WalletAccount[];
  mappings: WalletAccountMapping[];
  groups: GroupOption[];
  connectionId: string;
  userId: string;
  onCreateMapping: (data: {
    connectionId: string;
    walletAccountId: string;
    walletAccountName: string;
    walletCurrency: string;
    groupId: string;
    userId: string;
  }) => void;
  onUpdateMapping: (mappingId: string, updates: { is_active?: boolean; auto_approve?: boolean; group_id?: string }) => void;
  onDeleteMapping: (mappingId: string) => void;
  /** Re-fetch the account list from Wallet (accounts are created in the Wallet app). */
  onRefreshAccounts: () => void;
  isRefreshingAccounts: boolean;
  /** Wallet reported that its API data is still syncing, so a new account may lag behind. */
  syncInProgress: boolean;
}

export function WalletAccountMappingList({
  accounts,
  mappings,
  groups,
  connectionId,
  userId,
  onCreateMapping,
  onUpdateMapping,
  onDeleteMapping,
  onRefreshAccounts,
  isRefreshingAccounts,
  syncInProgress,
}: WalletAccountMappingListProps) {
  const { t } = useTranslation();
  const [selectedGroups, setSelectedGroups] = useState<Record<string, string>>({});

  // Build lookup of existing mappings by wallet account ID
  const mappingByAccountId = new Map(
    mappings.map(m => [m.wallet_account_id, m]),
  );

  const handleMapAccount = (account: WalletAccount) => {
    const groupId = selectedGroups[account.id];
    if (!groupId) return;

    onCreateMapping({
      connectionId,
      walletAccountId: account.id,
      walletAccountName: account.name,
      walletCurrency: account.currencyCode,
      groupId,
      userId,
    });

    // Clear selection
    setSelectedGroups(prev => {
      const next = { ...prev };
      delete next[account.id];
      return next;
    });
  };

  return (
    <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link2 className="h-5 w-5 text-text-quaternary" />
          <h3 className="text-lg font-semibold text-text-primary">{t('web:walletAccountMappingList.accountMappings')}</h3>
        </div>
        <button
          onClick={onRefreshAccounts}
          disabled={isRefreshingAccounts}
          className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshingAccounts ? 'animate-spin' : ''}`} />
          {t('web:walletAccountMappingList.refreshAccounts')}
        </button>
      </div>

      <p className="mt-2 text-sm text-text-tertiary">
        {t('web:walletAccountMappingList.mapYourWalletBankAccountsTo')}{' '}
        {t('web:walletAccountMappingList.refreshHint')}
      </p>

      {syncInProgress && (
        <p className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 p-2 text-xs text-amber-800 dark:text-amber-400">
          {t('web:walletAccountMappingList.walletStillSyncing')}
        </p>
      )}

      <div className="mt-4 space-y-3">
        {accounts.map(account => {
          const existingMapping = mappingByAccountId.get(account.id);

          return (
            <div
              key={account.id}
              className="rounded-lg border border-border-primary p-4"
            >
              {/* Account info header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary">{account.name}</p>
                  <p className="text-xs text-text-tertiary">
                    {account.currencyCode} &middot; {account.accountType}
                  </p>
                </div>

                {existingMapping && (
                  <div className="flex items-center gap-2">
                    {/* Auto-approve toggle */}
                    <button
                      onClick={() =>
                        onUpdateMapping(existingMapping.id, {
                          auto_approve: !existingMapping.auto_approve,
                        })
                      }
                      className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors ${
                        existingMapping.auto_approve
                          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : 'bg-surface-secondary text-text-tertiary'
                      }`}
                      title={existingMapping.auto_approve ? t('web:walletAccountMappingList.autoApproveEnabled') : t('web:walletAccountMappingList.autoApproveDisabled')}
                    >
                      {existingMapping.auto_approve ? (
                        <><Zap className="h-3 w-3" /> {t('web:walletAccountMappingList.auto')}</>
                      ) : (
                        <><ZapOff className="h-3 w-3" /> {t('web:walletAccountMappingList.manual')}</>
                      )}
                    </button>

                    {/* Active toggle */}
                    <button
                      onClick={() =>
                        onUpdateMapping(existingMapping.id, {
                          is_active: !existingMapping.is_active,
                        })
                      }
                      className="text-text-quaternary hover:text-text-secondary"
                      title={existingMapping.is_active ? t('web:walletAccountMappingList.pauseMapping') : t('web:walletAccountMappingList.resumeMapping')}
                    >
                      {existingMapping.is_active ? (
                        <ToggleRight className="h-5 w-5 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </button>

                    {/* Delete mapping */}
                    <button
                      onClick={() => onDeleteMapping(existingMapping.id)}
                      className="text-text-quaternary hover:text-red-500 dark:hover:text-red-400"
                      title={t('web:walletAccountMappingList.removeMapping')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Mapping target */}
              {existingMapping ? (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-text-quaternary">&rarr;</span>
                  <div
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: existingMapping.group?.color ?? '#6366F1' }}
                  />
                  <span className="font-medium text-text-secondary">
                    {existingMapping.group?.name ?? t('web:walletAccountMappingList.unknownGroup')}
                  </span>
                  {!existingMapping.is_active && (
                    <span className="rounded bg-surface-tertiary px-1.5 py-0.5 text-xs text-text-tertiary">
                      {t('web:walletAccountMappingList.paused')}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <select
                    value={selectedGroups[account.id] ?? ''}
                    onChange={e =>
                      setSelectedGroups(prev => ({ ...prev, [account.id]: e.target.value }))
                    }
                    className="flex-1 rounded-lg border border-border-secondary px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">{t('web:walletAccountMappingList.selectAGroup')}</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.currency_code})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleMapAccount(account)}
                    disabled={!selectedGroups[account.id]}
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
                  >
                    {t('web:walletAccountMappingList.map')}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {accounts.length === 0 && (
          <p className="text-sm text-text-tertiary">
            {t('web:walletAccountMappingList.noAccountsFoundConnectYourWallet')}
          </p>
        )}
      </div>
    </div>
  );
}
